import React from "react";
import LegalLayout from "@/components/legal/LegalLayout";

const INTRO = [
  "This Privacy Policy explains how PAYD LLC collects, uses, discloses, and protects information when you use PAYD.",
];

const SECTIONS = [
  {
    id: "information-we-collect",
    title: "Information We Collect",
    blocks: [
      { type: "p", text: "We may collect:" },
      { type: "ul", items: [
        "account information, such as name, email address, username, business name, and login details",
        "transaction-related information, such as invoices, milestones, wallet addresses, payment status, and dispute materials",
        "blockchain information, including public wallet addresses and on-chain transaction data",
        "communications, including messages, support requests, and dispute submissions",
        "device and usage information, including IP address, browser type, device type, pages viewed, log data, and analytics data",
        "compliance information, if required, such as identity, sanctions, fraud-prevention, or tax-related information",
      ] },
    ],
  },
  {
    id: "blockchain-public",
    title: "Blockchain Data Is Public",
    blocks: [
      { type: "p", text: "Transactions on blockchain networks may be public, permanent, and not controlled by PAYD." },
      { type: "p", text: "PAYD cannot delete, reverse, hide, or modify public blockchain records." },
    ],
  },
  {
    id: "how-we-use",
    title: "How We Use Information",
    blocks: [
      { type: "p", text: "We may use information to:" },
      { type: "ul", items: [
        "provide and operate PAYD",
        "create accounts and authenticate users",
        "process invoices, milestones, escrow workflows, and payment confirmations",
        "detect fraud, abuse, Sybil activity, suspicious transactions, and prohibited conduct",
        "maintain platform security",
        "respond to support requests",
        "improve the platform",
        "comply with legal, tax, regulatory, sanctions, and law-enforcement obligations",
        "enforce our Terms of Service",
      ] },
    ],
  },
  {
    id: "how-we-share",
    title: "How We Share Information",
    blocks: [
      { type: "p", text: "We may share information with:" },
      { type: "ul", items: [
        "counterparties to a transaction, as needed to operate invoices, milestones, payments, and disputes",
        "service providers, including hosting, analytics, security, wallet, blockchain infrastructure, compliance, and customer support providers",
        "legal, regulatory, tax, law-enforcement, or governmental authorities when required or appropriate",
        "professional advisors, including attorneys, accountants, auditors, and insurers",
        "business transfer parties in connection with a merger, acquisition, financing, restructuring, or sale of assets",
      ] },
      { type: "p", text: "We do not sell personal information in the ordinary meaning of selling customer lists for money." },
    ],
  },
  {
    id: "cookies",
    title: "Cookies and Analytics",
    blocks: [
      { type: "p", text: "We may use cookies, pixels, SDKs, log files, and analytics tools to operate, secure, measure, and improve PAYD." },
      { type: "p", text: "You may control cookies through your browser settings, but disabling cookies may affect platform functionality." },
    ],
  },
  {
    id: "security",
    title: "Security",
    blocks: [
      { type: "p", text: "We use reasonable administrative, technical, and organizational measures designed to protect information." },
      { type: "p", text: "No system, wallet, blockchain, smart contract, or internet transmission is completely secure. PAYD does not guarantee absolute security." },
    ],
  },
  {
    id: "retention",
    title: "Data Retention",
    blocks: [
      { type: "p", text: "We retain information as long as reasonably necessary to operate PAYD, comply with legal obligations, resolve disputes, enforce agreements, prevent fraud, and maintain business records." },
      { type: "p", text: "Blockchain records may persist indefinitely outside PAYD's control." },
    ],
  },
  {
    id: "your-choices",
    title: "Your Choices",
    blocks: [
      { type: "p", text: "Depending on your location, you may have rights to access, correct, delete, or restrict certain personal information." },
      { type: "p", text: "Some requests may be limited by legal, security, fraud-prevention, tax, contractual, or blockchain-related requirements." },
    ],
  },
  {
    id: "children",
    title: "Children",
    blocks: [
      { type: "p", text: "PAYD is not intended for children under 18. We do not knowingly collect personal information from children." },
    ],
  },
  {
    id: "international",
    title: "International Users",
    blocks: [
      { type: "p", text: "If you access PAYD from outside the United States, you understand that your information may be processed in the United States or other jurisdictions where privacy laws may differ from those in your location." },
    ],
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    blocks: [
      { type: "p", text: "We may update this Privacy Policy from time to time. Continued use of PAYD after updates means you acknowledge the revised policy." },
    ],
  },
  {
    id: "contact",
    title: "Contact",
    blocks: [
      { type: "contact", name: "PAYD LLC", address: "282 Mountain Road, Pleasantville, NY, 10570", email: "hello@payd.day" },
    ],
  },
];

const Privacy = () => (
  <LegalLayout title="Privacy Policy" lastUpdated="June 28, 2026" intro={INTRO} sections={SECTIONS} />
);

export default Privacy;
